/**
 * Numbered levels that get harder as you go ("progression").
 *
 * Two questions have to be answered for each level number:
 * 1. What settings should it use? Bigger grids, more colors, faster blocks,
 *    and so on as the number rises. `TIERS` and `targetFor` decide this.
 * 2. Which random level, out of all the ones those settings could produce?
 *    Levels made with the same settings can still vary a lot in difficulty.
 *    So several candidates are generated, and the bot (bot.ts) plays each one
 *    to measure how often an average player would win. The first candidate
 *    whose measured "win rate" is in the target range is used.
 *
 * Candidates are made from seeds derived from the level number, so the same
 * level number always gives the same level for every player.
 *
 * Every third level (3, 6, 9, ...) is a picture level: its grid is one of the
 * built-in pictures (pictures.ts), smallest first. Its other settings come
 * from its tier as usual. Once every picture has been used, they come round
 * again, with the harder settings of later tiers.
 */
import { winRate as measureWinRate } from "./bot";
import {
	generateLevel,
	type LevelOptions,
	MAX_BLOCK_COUNT,
	suggestedMaxCount,
} from "./level";
import { PALETTE } from "./palette";
import { PICTURE_COUNT, picture } from "./pictures";
import type { Cell, GameState } from "./state";

/** Every this-many levels is a picture level. */
export const PICTURE_EVERY = 3;

/**
 * Which picture a level number uses (its position in pictures.ts), or `null`
 * if it's an ordinary random level. Pictures repeat once they've all been used.
 */
export function pictureFor(level: number): number | null {
	if (level % PICTURE_EVERY !== 0) return null;
	return (level / PICTURE_EVERY - 1) % PICTURE_COUNT;
}

/**
 * Grid size and column count grow gradually from level 1 to this level, then
 * stay at their maximum.
 */
export const RAMP_LEVELS = 30;

/**
 * A named band of levels ("Easy", "Medium", ...) with its own settings and
 * difficulty target. The name is shown to the player.
 */
export interface Tier {
	name: string;
	/** The first level number in this tier. A tier lasts until the next one begins. */
	from: number;
	/**
	 * Settings that change from tier to tier. Grid size and column count grow
	 * smoothly across all levels instead (see `RAMP_LEVELS`).
	 *
	 * Settings written as a pair, `[a, b]`, change gradually within the tier: `a`
	 * at its first level, `b` at its last.
	 */
	colors: [number, number];
	traySize: number;
	maxRunners: number;
	clustering: number;
	/** Chance the solution parks blocks in the tray. See `LevelOptions.parking` in level.ts. */
	parking: number;
	/** How many blocks to put out of order in their columns. See `LevelOptions.mixing`. */
	mixing: number;
	/** Chance the solution uses two-trip blocks. See `LevelOptions.twoTrip`. */
	twoTrip: number;
	links: [number, number];
	linkSize: number;
	/**
	 * Block speed in track slots per second, from the tier's first level to its
	 * last. Faster blocks leave the player less time to think. The bot doesn't
	 * feel time pressure, so this makes levels harder for people in a way the
	 * win-rate measurement can't see.
	 */
	speed: [number, number];
	/**
	 * The target range for the bot's win rate, from the tier's first level to
	 * its last. Higher means easier.
	 */
	winRate: [number, number];
}

/**
 * The difficulty tiers, easiest first. Changing how the game's difficulty
 * ramps up mostly means editing the numbers in this table.
 */
export const TIERS: Tier[] = [
	{
		name: "Easy",
		from: 1,
		colors: [2, 3],
		traySize: 2,
		maxRunners: 2,
		clustering: 0,
		parking: 0,
		mixing: 0,
		twoTrip: 0,
		links: [0, 0],
		linkSize: 2,
		speed: [7, 8],
		winRate: [1, 0.95],
	},
	{
		name: "Medium",
		from: 8,
		colors: [3, 5],
		traySize: 3,
		maxRunners: 3,
		clustering: 2,
		parking: 0.15,
		mixing: 1,
		twoTrip: 0.1,
		links: [1, 2],
		linkSize: 2,
		speed: [9, 11],
		winRate: [0.95, 0.85],
	},
	{
		name: "Hard",
		from: 16,
		colors: [4, 6],
		traySize: 4,
		maxRunners: 4,
		clustering: 2,
		parking: 0.3,
		mixing: 2,
		twoTrip: 0.2,
		links: [2, 3],
		linkSize: 2,
		speed: [12, 14],
		winRate: [0.85, 0.72],
	},
	{
		name: "Expert",
		from: 25,
		colors: [5, 6],
		traySize: 5,
		maxRunners: 5,
		clustering: 3,
		parking: 0.45,
		mixing: 3,
		twoTrip: 0.3,
		links: [3, 4],
		linkSize: 3,
		speed: [15, 17],
		winRate: [0.72, 0.6],
	},
	{
		name: "Master",
		from: 31,
		colors: [6, 8],
		traySize: 5,
		maxRunners: 5,
		clustering: 3,
		parking: 0.6,
		mixing: 4,
		twoTrip: 0.4,
		links: [4, 4],
		linkSize: 3,
		speed: [18, 20],
		winRate: [0.6, 0.45],
	},
	{
		// Starts exactly where Master ends, then keeps getting harder: see `ENDLESS`.
		name: "Legend",
		from: 41,
		colors: [8, 8],
		traySize: 5,
		maxRunners: 5,
		clustering: 3,
		parking: 0.6,
		mixing: 4,
		twoTrip: 0.4,
		links: [4, 4],
		linkSize: 3,
		speed: [20, 20],
		winRate: [0.45, 0.45],
	},
];

/**
 * How the last tier keeps getting harder, with no end: every `every` levels
 * into it, each setting changes by `step`, until it reaches `limit`. The
 * change is spread across the levels in between (whole-number settings are
 * rounded), so there are no sudden jumps. Most limits are reached about 100
 * levels in.
 */
export const ENDLESS = {
	every: 10,
	/** Width and height of random grids (picture levels keep the picture's size). */
	grid: { step: 1, limit: 16 },
	colors: { step: 1, limit: 12 },
	columns: { step: 0.5, limit: 7 },
	speed: { step: 2, limit: 30 },
	mixing: { step: 1, limit: 8 },
	links: { step: 1, limit: 8 },
	parking: { step: 0.05, limit: 0.8 },
	twoTrip: { step: 0.05, limit: 0.6 },
	/** The target bot win rate goes down, so its step is negative and its limit is a floor. */
	winRate: { step: -0.03, limit: 0.25 },
};

/**
 * How many levels a tier spans. The last tier has no end, so it's treated as
 * 10 levels long for ramping purposes and then stays at its final values.
 */
function tierSpan(index: number): number {
	const next = TIERS[index + 1];
	return next ? next.from - (TIERS[index] as Tier).from : 10;
}

/** Find which tier a level number belongs to (the last tier whose `from` is at or below it). */
export function tierFor(level: number): Tier {
	return (
		[...TIERS].reverse().find((t) => level >= t.from) ?? (TIERS[0] as Tier)
	);
}

/**
 * What to generate for one level: its settings, and the range of bot win
 * rates that counts as the right difficulty.
 */
export interface Target {
	options: Omit<LevelOptions, "seed">;
	/** For a picture level, the picture's name and grid cells; otherwise `undefined`. */
	picture?: { name: string; cells: Cell[] };
	/** Accepted range of the bot's win rate (0 to 1). Higher means easier. */
	winRate: { min: number; max: number };
}

/**
 * Work out the settings and difficulty target for a level number (starting
 * at 1). Combines the level's tier with how far through the tier it is.
 */
export function targetFor(level: number): Target {
	const tier = tierFor(level);
	const tierIndex = TIERS.indexOf(tier);
	// How far through its tier this level is (`u`, from 0 to 1), and how far
	// through the overall ramp (`t`, from 0 to 1). Used to blend between values.
	const u = Math.min(
		1,
		(level - tier.from) / Math.max(1, tierSpan(tierIndex) - 1),
	);
	const t = Math.min(1, (level - 1) / (RAMP_LEVELS - 1));
	const within = ([a, b]: [number, number]) => Math.round(a + (b - a) * u);
	const ramp = (a: number, b: number) => Math.round(a + (b - a) * t);

	// A picture level's grid is the picture, so the picture decides its size
	// and colors.
	const index = pictureFor(level);
	const art = index === null ? null : picture(index);
	// Past the start of the last tier, settings keep growing (see ENDLESS).
	// `k` is how many steps in: 0 at the tier's first level, 1 after 10 levels.
	const k =
		tierIndex === TIERS.length - 1 ? (level - tier.from) / ENDLESS.every : 0;
	const grow = (
		base: number,
		{ step, limit }: { step: number; limit: number },
	) =>
		step > 0
			? Math.min(limit, base + step * k)
			: Math.max(limit, base + step * k);
	const growWhole = (base: number, rule: { step: number; limit: number }) =>
		Math.round(grow(base, rule));

	const size = growWhole(ramp(5, 10), ENDLESS.grid);
	const width = art ? art.grid.width : size;
	const height = art ? art.grid.height : size;
	const colors = art
		? new Set(art.grid.cells.filter((c) => c !== null)).size
		: Math.min(PALETTE.length, growWhole(within(tier.colors), ENDLESS.colors));
	const columns = growWhole(ramp(2, 5), ENDLESS.columns);
	const center = grow(
		tier.winRate[0] + (tier.winRate[1] - tier.winRate[0]) * u,
		ENDLESS.winRate,
	);

	return {
		options: {
			width,
			height,
			colors,
			columns,
			// Block counts grow with the grid (see suggestedMaxCount), but less
			// generously later on: relatively smaller blocks mean more blocks, and
			// more decisions to make.
			maxCount: Math.min(
				MAX_BLOCK_COUNT,
				Math.round(suggestedMaxCount(width, height) * (1.3 - 0.5 * t)),
			),
			clustering: tier.clustering,
			parking: grow(tier.parking, ENDLESS.parking),
			mixing: growWhole(tier.mixing, ENDLESS.mixing),
			twoTrip: grow(tier.twoTrip, ENDLESS.twoTrip),
			links: growWhole(within(tier.links), ENDLESS.links),
			linkSize: Math.min(tier.linkSize, tier.maxRunners),
			traySize: tier.traySize,
			maxRunners: tier.maxRunners,
			speed: growWhole(within(tier.speed), ENDLESS.speed),
		},
		picture: art ? { name: art.name, cells: art.grid.cells } : undefined,
		// For the easiest levels, the bot winning every game is fine: they're meant
		// to be gentle.
		winRate: {
			min: Math.max(0, center - 0.07),
			max: center >= 0.9 ? 1 : center + 0.07,
		},
	};
}

/** A level chosen for a level number, with its settings and measured difficulty. */
export interface ProgressionLevel {
	level: number;
	state: GameState;
	options: LevelOptions;
	/** For a picture level, the picture's name and grid cells (pass them to `generateLevel` to rebuild it). */
	picture?: { name: string; cells: Cell[] };
	/** How often the average-skill bot won it (0 to 1). Higher means easier. */
	winRate: number;
}

/** How many candidates to try before settling for the closest match. */
const MAX_ATTEMPTS = 30;
/**
 * The fewest candidates tried, however big the grid. Big picture levels (up
 * to 64x64) take far longer to generate and play out, so they try fewer
 * candidates: about as many as fit in the time of 30 small ones (see
 * `attemptsFor`).
 */
const MIN_ATTEMPTS = 3;

/** How many candidates to try for a grid of this many cells. */
export function attemptsFor(cells: number): number {
	return Math.max(
		MIN_ATTEMPTS,
		Math.min(MAX_ATTEMPTS, Math.round((MAX_ATTEMPTS * 256) / cells)),
	);
}

/**
 * A different seed for each level number and attempt. Multiplying by large
 * prime numbers spreads the seeds out so nearby levels don't get similar
 * seeds. `>>> 0` keeps the result a positive 32-bit whole number.
 */
function seedFor(level: number, attempt: number): number {
	return (Math.imul(level, 7919) + Math.imul(attempt, 104_729)) >>> 0;
}

/**
 * Choose the level for a level number: try candidates in turn and use the
 * first whose measured win rate is in the target range, or the closest one
 * if none is.
 *
 * Every candidate is already guaranteed winnable (see level.ts), so this
 * search is only about difficulty. Each candidate takes a few milliseconds
 * to generate and test, so a whole search can take a few hundred. The
 * biggest picture levels take up to about a second, even with fewer
 * candidates (see `attemptsFor`).
 */
export function generateProgressionLevel(
	level: number,
	playouts = 16,
): ProgressionLevel {
	const { options, winRate, picture } = targetFor(level);
	const center = (winRate.min + winRate.max) / 2;
	let best: ProgressionLevel | null = null;

	const attempts = attemptsFor(options.width * options.height);
	for (let attempt = 0; attempt < attempts; attempt++) {
		const full: LevelOptions = { ...options, seed: seedFor(level, attempt) };
		const state = generateLevel(full, picture?.cells);
		const rate = measureWinRate(state, playouts, full.seed);
		const candidate = { level, state, options: full, picture, winRate: rate };
		if (rate >= winRate.min && rate <= winRate.max) return candidate;
		if (!best || Math.abs(rate - center) < Math.abs(best.winRate - center))
			best = candidate;
	}
	return best as ProgressionLevel;
}
